import { Button, isoDay, pickerDay } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { DatePicker } from "@majitap/ui/arc/date-picker/date-picker";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { refreshCounts } from "../../counts";
import { boardQuery } from "../../dispatch";
import { messageFor } from "../../error-copy";
import { codeFromName, type Incident, type IncidentType, incidentActions, incidentTypesQuery } from "../../incidents";
import { DELIVERY_STATUS } from "../../labels";
import styles from "../dispatch/dispatch.module.css";

function useRefresh() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["incidents"] });
    await queryClient.invalidateQueries({ queryKey: ["incident-types"] });
    await queryClient.invalidateQueries({ queryKey: ["dispatch-board"] });
    // a delivery's page lists its incidents (P8-48)
    await queryClient.invalidateQueries({ queryKey: ["delivery"] });
    await refreshCounts(queryClient);
  };
}

/**
 * Log an incident on a delivery. From a Dispatch row the delivery is given; from Incidents it's
 * picked from any day's deliveries, delivered ones too (a complaint can come after).
 */
export function LogIncidentDialog({
  delivery,
  onClose,
}: {
  delivery?: { id: string; delivery_number: string };
  onClose: () => void;
}) {
  const refresh = useRefresh();
  const format = useCompanyFormat();
  const types = useQuery(incidentTypesQuery);
  // any day's deliveries, delivered ones too: a complaint can come after (P8-31)
  const [day, setDay] = useState(() => isoDay(format.timezone));
  const today = useQuery({ ...boardQuery(day, {}), enabled: !delivery });
  const [deliveryId, setDeliveryId] = useState(delivery?.id ?? "");
  const [typeCode, setTypeCode] = useState("");
  const [notes, setNotes] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      incidentActions.report({ delivery_id: deliveryId, type_code: typeCode, notes: notes.trim() || undefined }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const open = (today.data?.rows ?? []).filter((d) => d.status !== "rerouted" && d.status !== "cancelled");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={delivery ? `Log an incident on ${delivery.delivery_number}` : "Log an incident"}>
        <div className={styles.form}>
          {delivery ? null : (
            <div className={styles.pair}>
              <DatePicker
                label="Day"
                value={new Date(`${day}T00:00:00`)}
                onChange={(d) => {
                  if (d) setDay(pickerDay(d));
                  setDeliveryId("");
                }}
                showToday
              />
              <Select
                label="Delivery"
                placeholder={open.length ? "Choose a delivery" : "No deliveries that day"}
                value={deliveryId}
                onValueChange={setDeliveryId}
                options={open.map((d) => ({
                  value: d.id,
                  label: `${d.delivery_number} · ${d.order.customer_name} · ${DELIVERY_STATUS[d.status][0]}`,
                }))}
              />
            </div>
          )}
          <Select
            label="What happened"
            placeholder="Choose a type"
            value={typeCode}
            onValueChange={setTypeCode}
            options={(types.data ?? []).filter((t) => t.is_active).map((t) => ({ value: t.code, label: t.name }))}
          />
          <Textarea
            label="Notes"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            description="What the driver or customer said, and what's being done."
          />
          {mutation.error ? (
            <Alert tone="danger" title="Not logged">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!deliveryId || !typeCode} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              Log incident
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Close an incident, saying how. It can only be resolved once (INCIDENT_RESOLVED). */
export function ResolveDialog({
  incident,
  onClose,
}: {
  // an incident from the list, or from a delivery's page (P8-48)
  incident: Pick<Incident, "id" | "notes" | "delivery_number"> & { type: { name: string } };
  onClose: () => void;
}) {
  const refresh = useRefresh();
  const [notes, setNotes] = useState("");
  const [missing, setMissing] = useState<string>();
  const mutation = useMutation({
    mutationFn: () => incidentActions.resolve(incident.id, notes.trim()),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Resolve: ${incident.type.name}`}
        description={`${incident.delivery_number}${incident.notes ? ` · ${incident.notes}` : ""}`}
      >
        <div className={styles.form}>
          <Textarea
            label="How it was resolved"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            error={missing}
          />
          {mutation.error ? (
            <Alert tone="danger" title="Not resolved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              loading={mutation.isPending}
              onClick={() =>
                notes.trim() ? mutation.mutate() : setMissing("Say how it was resolved, for the record.")
              }
            >
              Resolve
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Add an incident type, or rename, describe or switch off an existing one. Types can't be deleted. */
export function IncidentTypeDialog({ type, onClose }: { type?: IncidentType; onClose: () => void }) {
  const refresh = useRefresh();
  const [name, setName] = useState(type?.name ?? "");
  const [description, setDescription] = useState(type?.description ?? "");
  const [active, setActive] = useState(type?.is_active ?? true);
  const code = type?.code ?? codeFromName(name);
  const mutation = useMutation({
    mutationFn: () =>
      type
        ? incidentActions.updateType(type.id, { name: name.trim(), description: description.trim(), is_active: active })
        : incidentActions.addType({ code, name: name.trim(), description: description.trim() || undefined }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  const error = mutation.error ? errorOf(mutation.error) : null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={type ? `Edit "${type.name}"` : "Add an incident type"}>
        <div className={styles.form}>
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            description={
              code
                ? `Code: ${code}${type ? " (codes don't change)" : ""}`
                : "Drivers and dispatchers pick it by this name."
            }
            error={error?.code === "DUPLICATE_ENTRY" ? "There's already a type with this name." : undefined}
          />
          <Textarea
            label="Description (optional)"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {type ? <Switch label="In use" checked={active} onCheckedChange={setActive} /> : null}
          {type && !active ? (
            <p className={styles.hint}>Switched off, it can't be picked for new incidents. Past ones keep it.</p>
          ) : null}
          {error && error.code !== "DUPLICATE_ENTRY" ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(error)}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!name.trim() || !code} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {type ? "Save" : "Add type"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
